import MarketingLayout from '../components/marketing/MarketingLayout';
import { colors } from '../lib/colors';
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BookingWidget from '../components/BookingWidget';
import { Card, Title, Text, List, ListItem, AreaChart, Metric, Flex, Badge, Grid, Col } from "@tremor/react";
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ShieldIcon from '@mui/icons-material/Shield';
import TimerIcon from '@mui/icons-material/Timer';
// Import HeroIcons for better icons
import { 
  ShieldCheckIcon, 
  BanknotesIcon, 
  BriefcaseIcon, 
  BuildingLibraryIcon,
  ChartBarIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

// Import modular components and data
import {
  // Import the new service arrays
  insuranceServices,
  savingsRetirementServices,
  businessPlanningServices,
  estateLegacyServices,
  advancedWealthServices,
  Service,
  wealthGrowthData, // Represents one potential outcome, use with context
  testimonials,
  slugify,
  allServicesForExplorer, // Use this for the main explorer
  serviceCategories // Import categories for the explorer - Note: This might need adjustment if filtering UI uses it
} from '../data/servicesData';
// Removed ServiceExplorer import as it's not used currently
import ServiceDetailModal from '../components/services/ServiceDetailModal';
import FloatingActionMenu from '../components/services/FloatingActionMenu';
import ServiceWizard from '../components/services/ServiceWizard';

export default function Services() {
  // State management
  const [showBookingWidget, setShowBookingWidget] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null); // State to hold data for the service clicked, triggering the modal
  const [showFloatingMenu, setShowFloatingMenu] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isWizardModalOpen, setIsWizardModalOpen] = useState(false); // State for ServiceWizard modal

  // State for sticky header visibility and dark section detection
  const [showStickyHeader, setShowStickyHeader] = useState(true);
  const [isDarkSectionVisible, setIsDarkSectionVisible] = useState(false);
  const [scrollDirection, setScrollDirection] = useState('none');
  
  // Handle scroll events for floating menu and sticky header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Determine scroll direction
      const direction = currentScrollY > lastScrollY ? 'down' : 'up';
      setScrollDirection(direction);
      
      // Show floating menu when scrolling up after passing 300px threshold
      setShowFloatingMenu(currentScrollY > 300 && direction === 'up');
      
      // Check if dark section (performance metrics) is visible
      const darkSection = document.getElementById('performance-metrics');
      if (darkSection) {
        const darkSectionTop = darkSection.getBoundingClientRect().top;
        // If the dark section is about to enter the viewport (200px before it's visible)
        const isDarkSectionNearViewport = darkSectionTop < 200;
        setIsDarkSectionVisible(isDarkSectionNearViewport);
        
        // Hide header when dark section is visible and scrolling down
        // Show header when scrolling up or when not near dark section
        if (isDarkSectionNearViewport && direction === 'down') {
          setShowStickyHeader(false);
        } else if (direction === 'up') {
          setShowStickyHeader(true);
        } else if (!isDarkSectionNearViewport) {
          setShowStickyHeader(true);
        }
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Prevent scrolling when modal is open
  useEffect(() => {
    if (showBookingWidget || selectedService) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showBookingWidget, selectedService]); // Note: ServiceWizard handles its own overflow
  
  // Get location state for scrolling to specific sections
  const location = useLocation();
  
  // Effect to handle scrolling to sections based on navigation state
  useEffect(() => {
    // Check if we have a scrollToId in the location state
    if (location.state && location.state.scrollToId) {
      // Small delay to ensure the page is fully loaded
      setTimeout(() => {
        scrollToSection(location.state.scrollToId);
      }, 100);
    }
  }, [location]);

  // Use the combined services list for the explorer
  const allServices = allServicesForExplorer;

  // Smooth scroll handler with offset for sticky header
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Get the height of the sticky header
      const header = document.querySelector('.sticky');
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      
      // Calculate the element's position relative to the top of the document
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      
      // Use a consistent offset for all sections now that they're symmetrical
      const buffer = 0; // No buffer needed with our standardized section padding
      
      // Add a small delay to ensure accurate positioning after any layout shifts
      setTimeout(() => {
        window.scrollTo({
          top: elementPosition - headerHeight - buffer,
          behavior: 'smooth'
        });
      }, 10);
    }
  };

  // Format number for chart
  const dataFormatter = (number: number) =>
    `$${Intl.NumberFormat('us').format(number).toString()}`;

  // Premium gradient background style applied consistently across sections
  const gradientStyle = {
    background: `radial-gradient(circle at center, ${colors.premium.dark} 0%, ${colors.premium.darker} 100%)`
  };

  // Enhanced background effects for sections
  const backgroundEffects = (
    <div className="absolute inset-0 opacity-20 pointer-events-none">
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-500/10 to-purple-600/10"></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500/20 rounded-full filter blur-3xl"></div>
    </div>
  );

  // Custom styles for better contrast and consistency
  const customStyles = {
    headingText: {
      color: '#FFFFFF',
      textShadow: '0 2px 4px rgba(0,0,0,0.3)'
    },
    accentText: {
      color: '#4DA8FF', // Brighter blue for better visibility
    },
    bodyText: {
      color: '#E6E6E6', // Lighter gray for better readability
    },
    card: {
      background: 'rgba(13, 17, 23, 0.7)',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
      transition: 'all 0.3s ease'
    }
  };

  // Helper function to render service cards consistently - Mobile Optimized
  // This card acts as the entry point to view service details.
  const renderServiceCard = (service: Service, index: number) => (
    <div
      key={index}
      onClick={() => setSelectedService(service)} // Clicking the card sets the state, which triggers the ServiceDetailModal display
      className="bg-premium-darker/70 hover:bg-premium-dark/90 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg group h-full flex flex-col touch-manipulation" // Added touch-manipulation for better mobile touch
      style={customStyles.card}
    >
      <div className="p-4 md:p-5 flex flex-col flex-grow"> {/* Reduced padding on mobile */}
        <Badge
          size="xs"
          // Update badge colors and text based on new categories
          color={
            service.category === 'insurance' ? 'blue' :
            service.category === 'savings_retirement' ? 'yellow' :
            service.category === 'business_planning' ? 'purple' :
            service.category === 'estate_legacy' ? 'indigo' :
            service.category === 'advanced_wealth' ? 'violet' : 'gray'
          }
          className={`mb-2 md:mb-3 text-white text-xs bg-${
            service.category === 'insurance' ? 'blue' :
            service.category === 'savings_retirement' ? 'yellow' :
            service.category === 'business_planning' ? 'purple' :
            service.category === 'estate_legacy' ? 'indigo' :
            service.category === 'advanced_wealth' ? 'violet' : 'gray'
          }-600/80 border border-${
            service.category === 'insurance' ? 'blue' :
            service.category === 'savings_retirement' ? 'yellow' :
            service.category === 'business_planning' ? 'purple' :
            service.category === 'estate_legacy' ? 'indigo' :
            service.category === 'advanced_wealth' ? 'violet' : 'gray'
          }-400/30 capitalize`} // Keep capitalize for display
        >
          {/* Display user-friendly category names */}
          {service.category === 'insurance' ? 'Insurance' :
           service.category === 'savings_retirement' ? 'Savings & Retirement' :
           service.category === 'business_planning' ? 'Business Planning' :
           service.category === 'estate_legacy' ? 'Estate & Legacy' :
           service.category === 'advanced_wealth' ? 'Advanced Wealth' : service.category}
        </Badge>
        <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-white">{service.title}</h3>
        <p className="text-gray-200 text-xs md:text-sm mb-3 md:mb-4 flex-grow">{service.description}</p> {/* Smaller text on mobile */}
        <div className="mt-auto pt-2 text-[#4DA8FF] text-xs flex items-center group-hover:translate-x-1 transition-transform">
          <span>Learn more</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );

  const handleCardClick = (section: string) => {
    // Scroll to the relevant section or navigate if needed
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <MarketingLayout>
      {/* Floating Action Menu Component */}
      <FloatingActionMenu 
        visible={showFloatingMenu}
        // onExploreClick={() => scrollToSection('service-explorer')} // Removed as section is deleted
        onBookingClick={() => setShowBookingWidget(true)}
      />

      {/* Skip to main content for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 z-50 bg-blue-600 text-white px-4 py-2 rounded-md"
      >
        Skip to main content
      </a>

      {/* Hero Section - Mobile Optimized */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden" style={gradientStyle}>
        {backgroundEffects}
        <div className="container mx-auto px-4 py-12 md:py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* <Badge color="blue" className="mb-3 md:mb-4 text-white bg-blue-600/80 border border-blue-400/30 text-xs md:text-sm">Solutions</Badge> */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6" style={customStyles.headingText}>
              Smart Strategies for <span className="block mt-1" style={customStyles.accentText}>Life & Business</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl mb-6 md:mb-8" style={customStyles.bodyText}>
              Tailored Insurance, Investment, and Planning Strategies Designed for Your Unique Goals.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contact">
                <button 
                  className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all hover:opacity-90 text-base"
                >
                  Book a Consultation
                </button>
              </Link>
              <button 
                onClick={() => setIsWizardModalOpen(true)}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-medium hover:shadow-lg transition-all hover:opacity-90 text-base flex items-center justify-center gap-2"
              >
                <span className="relative overflow-hidden">
                  <span className="absolute -rotate-12 -translate-x-full top-0 w-1/3 h-full bg-gradient-to-r from-transparent via-amber-300/30 to-transparent transform animate-shine"></span>
                </span>
                Find Your Service
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>



      {/* Main Content */}
      <main id="main-content" className="relative">
        {/* Service Navigation Header - Sticky with Apple-style Animation */}
        <div className={`sticky top-0 z-20 bg-white shadow-md border-b border-gray-200 transition-all duration-500 ease-in-out ${showStickyHeader ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
          <div className="container mx-auto px-4">
            <div className="py-2 md:py-3">
              <h2 className="text-lg font-bold text-gray-800 mb-1 text-center">How Can We Help?</h2>
              <p className="text-xs text-gray-500 mb-2 text-center max-w-2xl mx-auto">Choose a category below to find the right solutions for your needs.</p>
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex justify-center items-center space-x-4">
                <a 
                  href="#insurance-section" 
                  onClick={(e) => { e.preventDefault(); scrollToSection('insurance-section'); }}
                  className="flex items-center px-4 py-1.5 text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-all duration-200 group border border-transparent hover:border-blue-200"
                >
                  <ShieldCheckIcon className="h-5 w-5 mr-2 text-blue-600" />
                  <span className="font-medium group-hover:translate-x-1 transition-transform duration-200">Insurance</span>
                  <ArrowRightIcon className="h-4 w-4 ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-blue-600" />
                </a>
                <a 
                  href="#savings-retirement-section" 
                  onClick={(e) => { e.preventDefault(); scrollToSection('savings-retirement-section'); }}
                  className="flex items-center px-4 py-1.5 text-gray-700 hover:bg-green-50 hover:text-green-700 rounded-lg transition-all duration-200 group border border-transparent hover:border-green-200"
                >
                  <BanknotesIcon className="h-5 w-5 mr-2 text-green-600" />
                  <span className="font-medium group-hover:translate-x-1 transition-transform duration-200">Savings & Retirement</span>
                  <ArrowRightIcon className="h-4 w-4 ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-green-600" />
                </a>
                <a 
                  href="#business-planning" 
                  onClick={(e) => { e.preventDefault(); scrollToSection('business-planning'); }}
                  className="flex items-center px-4 py-1.5 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg transition-all duration-200 group border border-transparent hover:border-indigo-200"
                >
                  <BriefcaseIcon className="h-5 w-5 mr-2 text-indigo-600" />
                  <span className="font-medium group-hover:translate-x-1 transition-transform duration-200">Business Planning</span>
                  <ArrowRightIcon className="h-4 w-4 ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-indigo-600" />
                </a>
                <a 
                  href="#estate-legacy" 
                  onClick={(e) => { e.preventDefault(); scrollToSection('estate-legacy'); }}
                  className="flex items-center px-4 py-1.5 text-gray-700 hover:bg-purple-50 hover:text-purple-700 rounded-lg transition-all duration-200 group border border-transparent hover:border-purple-200"
                >
                  <BuildingLibraryIcon className="h-5 w-5 mr-2 text-purple-600" />
                  <span className="font-medium group-hover:translate-x-1 transition-transform duration-200">Estate & Legacy</span>
                  <ArrowRightIcon className="h-4 w-4 ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-purple-600" />
                </a>
                <a 
                  href="#advanced-wealth" 
                  onClick={(e) => { e.preventDefault(); scrollToSection('advanced-wealth'); }}
                  className="flex items-center px-4 py-1.5 text-gray-700 hover:bg-violet-50 hover:text-violet-700 rounded-lg transition-all duration-200 group border border-transparent hover:border-violet-200"
                >
                  <ChartBarIcon className="h-5 w-5 mr-2 text-violet-600" />
                  <span className="font-medium group-hover:translate-x-1 transition-transform duration-200">Advanced Wealth</span>
                  <ArrowRightIcon className="h-4 w-4 ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-violet-600" />
                </a>
              </div>
              
              {/* Mobile Navigation - Horizontal Scrolling */}
              <div className="md:hidden overflow-x-auto whitespace-nowrap py-2">
                <div className="inline-flex space-x-2">
                  <a 
                    href="#insurance-section" 
                    onClick={(e) => { e.preventDefault(); scrollToSection('insurance-section'); }}
                    className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 border border-blue-200 shadow-sm"
                  >
                    <ShieldCheckIcon className="h-4 w-4 mr-1.5" />
                    Insurance
                  </a>
                  <a 
                    href="#savings-retirement-section" 
                    onClick={(e) => { e.preventDefault(); scrollToSection('savings-retirement-section'); }}
                    className="inline-flex items-center px-3.5 py-2 text-sm font-medium rounded-lg bg-green-50 text-green-700 border border-green-200 shadow-sm"
                  >
                    <BanknotesIcon className="h-4 w-4 mr-1.5" />
                    Savings
                  </a>
                  <a 
                    href="#business-planning" 
                    onClick={(e) => { e.preventDefault(); scrollToSection('business-planning'); }}
                    className="inline-flex items-center px-3.5 py-2 text-sm font-medium rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 shadow-sm"
                  >
                    <BriefcaseIcon className="h-4 w-4 mr-1.5" />
                    Business
                  </a>
                  <a 
                    href="#estate-legacy" 
                    onClick={(e) => { e.preventDefault(); scrollToSection('estate-legacy'); }}
                    className="inline-flex items-center px-3.5 py-2 text-sm font-medium rounded-lg bg-purple-50 text-purple-700 border border-purple-200 shadow-sm"
                  >
                    <BuildingLibraryIcon className="h-4 w-4 mr-1.5" />
                    Estate
                  </a>
                  <a 
                    href="#advanced-wealth" 
                    onClick={(e) => { e.preventDefault(); scrollToSection('advanced-wealth'); }}
                    className="inline-flex items-center px-3.5 py-2 text-sm font-medium rounded-lg bg-violet-50 text-violet-700 border border-violet-200 shadow-sm"
                  >
                    <ChartBarIcon className="h-4 w-4 mr-1.5" />
                    Advanced
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Insurance Section - Updated with professional styling */}
        <section id="insurance-section" className="pt-8 pb-16 bg-gray-50 border-t border-gray-200">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <span className="inline-block px-2.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full mb-2">Protection & Security</span>
              <h2 className="text-2xl md:text-3xl font-bold mb-2 text-gray-800">Insurance Services</h2>
              <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
                Comprehensive life, health, and disability coverage to protect you, your family, and your business.
              </p>
            </div>
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {insuranceServices.map((service, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedService(service)}
                    className="bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer transition-all hover:shadow-md h-full flex flex-col"
                  >
                    <div className="p-4 md:p-5 flex flex-col flex-grow">
                      <span className="text-xs font-medium text-blue-700 bg-blue-50 rounded-full px-2 py-0.5 inline-block w-fit mb-2">Insurance</span>
                      <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-gray-800">{service.title}</h3>
                      <p className="text-gray-600 text-xs md:text-sm mb-3 md:mb-4 flex-grow">{service.description}</p>
                      <div className="mt-auto pt-2 text-blue-600 text-xs flex items-center hover:translate-x-1 transition-transform">
                        <span>Learn more</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Savings & Retirement Section - Updated with professional styling */}
         <section id="savings-retirement-section" className="pt-8 pb-16 bg-white border-t border-gray-200">
             <div className="container mx-auto px-4">
               <div className="text-center mb-8">
                 <span className="inline-block px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full mb-2">Build Your Future</span>
                 <h2 className="text-2xl md:text-3xl font-bold mb-2 text-gray-800">Savings & Retirement</h2>
                 <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
                   Utilize tax-efficient accounts and strategies to grow your savings and plan for retirement income.
                 </p>
               </div>
               <div className="max-w-6xl mx-auto">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                     {savingsRetirementServices.map((service, index) => (
                       <div
                         key={index}
                         onClick={() => setSelectedService(service)}
                         className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden cursor-pointer transition-all hover:shadow-md hover:border-green-200 h-full flex flex-col"
                       >
                         <div className="p-4 md:p-5 flex flex-col flex-grow">
                           <span className="text-xs font-medium text-green-700 bg-green-50 rounded-full px-2 py-0.5 inline-block w-fit mb-2">Savings & Retirement</span>
                           <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-gray-800">{service.title}</h3>
                           <p className="text-gray-600 text-xs md:text-sm mb-3 md:mb-4 flex-grow">{service.description}</p>
                           <div className="mt-auto pt-2 text-green-600 text-xs flex items-center hover:translate-x-1 transition-transform">
                             <span>Learn more</span>
                             <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                             </svg>
                           </div>
                         </div>
                       </div>
                     ))}
                  </div>
               </div>
             </div>
         </section>

        {/* Planning Section - Updated with professional styling */}
        <section id="planning-section" className="pt-8 pb-16 bg-gray-50 border-t border-gray-200">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <span className="inline-block px-2.5 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full mb-2">Advanced Strategies</span>
              <h2 className="text-2xl md:text-3xl font-bold mb-2 text-gray-800">Business, Estate & Advanced Planning</h2>
              <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
                Sophisticated strategies for business continuity, estate preservation, tax optimization, and complex financial goals.
              </p>
            </div>
            <div className="max-w-6xl mx-auto">
              <div className="space-y-10">
                {/* Business Planning */}
                <div id="business-planning" className="pt-2">
                  <h3 className="text-xl font-semibold text-gray-700 mb-5 text-center">Business Planning</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {businessPlanningServices.map((service, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedService(service)}
                        className="bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer transition-all hover:shadow-md h-full flex flex-col"
                      >
                        <div className="p-4 md:p-5 flex flex-col flex-grow">
                          <span className="text-xs font-medium text-indigo-700 bg-indigo-50 rounded-full px-2 py-0.5 inline-block w-fit mb-2">Business Planning</span>
                          <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-gray-800">{service.title}</h3>
                          <p className="text-gray-600 text-xs md:text-sm mb-3 md:mb-4 flex-grow">{service.description}</p>
                          <div className="mt-auto pt-2 text-indigo-600 text-xs flex items-center hover:translate-x-1 transition-transform">
                            <span>Learn more</span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Estate & Legacy */}
                <div id="estate-legacy" className="pt-2">
                  <h3 className="text-xl font-semibold text-gray-700 mb-5 text-center">Estate & Legacy</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {estateLegacyServices.map((service, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedService(service)}
                        className="bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer transition-all hover:shadow-md h-full flex flex-col"
                      >
                        <div className="p-4 md:p-5 flex flex-col flex-grow">
                          <span className="text-xs font-medium text-purple-700 bg-purple-50 rounded-full px-2 py-0.5 inline-block w-fit mb-2">Estate & Legacy</span>
                          <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-gray-800">{service.title}</h3>
                          <p className="text-gray-600 text-xs md:text-sm mb-3 md:mb-4 flex-grow">{service.description}</p>
                          <div className="mt-auto pt-2 text-purple-600 text-xs flex items-center hover:translate-x-1 transition-transform">
                            <span>Learn more</span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Advanced Wealth */}
                <div id="advanced-wealth" className="pt-2">
                  <h3 className="text-xl font-semibold text-gray-700 mb-5 text-center">Advanced Wealth Strategies</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {advancedWealthServices.map((service, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedService(service)}
                        className="bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer transition-all hover:shadow-md h-full flex flex-col"
                      >
                        <div className="p-4 md:p-5 flex flex-col flex-grow">
                          <span className="text-xs font-medium text-blue-700 bg-blue-50 rounded-full px-2 py-0.5 inline-block w-fit mb-2">Advanced Wealth</span>
                          <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3 text-gray-800">{service.title}</h3>
                          <p className="text-gray-600 text-xs md:text-sm mb-3 md:mb-4 flex-grow">{service.description}</p>
                          <div className="mt-auto pt-2 text-blue-600 text-xs flex items-center hover:translate-x-1 transition-transform">
                            <span>Learn more</span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Performance Metrics Section - Mobile Optimized */}
        {/* This is the dark section that triggers the navigation animation */}
        <section id="performance-metrics" className="py-10 md:py-16 relative bg-premium-dark">
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-6 md:mb-10">
                <Badge color="blue" className="mb-3 md:mb-4 text-white bg-blue-600/80 border border-blue-400/30 text-xs md:text-sm">Analytics & Outcomes</Badge>
                <h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-white">
                  Illustrative Performance & Client Metrics
                </h2>
                <p className="text-sm md:text-base text-gray-300 font-medium">
                  We focus on delivering measurable results and exceptional client satisfaction. Our strategies are designed to create clear financial advantages while maintaining the highest standards of service.
                </p>
              </div>

              {/* Performance Chart - Keep context in mind */}
              <Card className="bg-premium-darker/70 border border-white/10 shadow-xl rounded-xl overflow-hidden" style={customStyles.card}>
                <div className="space-y-2 border-b border-white/10 pb-4">
                  <Title className="text-white text-xl">Illustrative Corporate Growth Comparison (20 Years)</Title>
                  <Text className="text-gray-400 text-sm">Scenario: $500k initial + $500k/yr contribution into Taxable Investment (6% gross, 50% tax) vs. Corporate-Owned Life Insurance (6.2% net).</Text>
                </div>

                <div className="mt-6 bg-premium-darkest/20 p-4 rounded-lg border border-white/5 shadow-inner">
                  <AreaChart
                    className="h-72"
                    data={wealthGrowthData}
                    index="year"
                    categories={["Taxable Investment", "Corp-Owned Insurance"]}
                    colors={["gray", "blue"]}
                    valueFormatter={dataFormatter}
                    showLegend={true}
                    showGridLines={false}
                    showYAxis={true}
                    showXAxis={true}
                    showAnimation={true}
                    curveType="monotone"
                  />
                </div>

                {/* Reorganized Grid for clearer comparison */}
                <div className="mt-6 space-y-4">
                  {/* Starting Value */}
                  <div className="text-center bg-premium-darker/60 py-3 rounded-lg border border-white/5 shadow-inner">
                      <Text className="text-gray-400 font-medium">Starting Value (Year 0)</Text>
                      <Metric className="text-white text-2xl mt-1">$500,000</Metric>
                  </div>
                  
                  {/* Comparison Grid - Enhanced layout and visual hierarchy */}
                  <div className="border-t border-white/10 pt-5 pb-2">
                    <Grid numItemsMd={3} className="gap-5 items-stretch"> 
                      {/* Taxable Investment Column */}
                      <Col className="bg-premium-darker/60 p-4 rounded-lg border border-white/5 shadow-md"> 
                        <Title className="text-lg text-gray-300 mb-3 text-center font-semibold">Taxable Investment</Title>
                        <div className="space-y-3 flex flex-col h-full">
                          <div className="bg-premium-darkest/40 p-3 rounded-md">
                            <Text className="text-gray-400 text-sm">Final Value (Year 20)</Text>
                            <Metric className="text-gray-400 text-xl mt-1">$14,338,244</Metric>
                          </div>
                          <div className="bg-premium-darkest/40 p-3 rounded-md">
                            <Text className="text-gray-400 text-sm">Est. Tax Impact</Text>
                            <Metric className="text-red-500 text-xl mt-1 flex items-center justify-center">
                              <span className="mr-1">~$5.66M</span>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                              </svg>
                            </Metric>
                          </div>
                        </div>
                      </Col>
                      
                      {/* VS Separator */}
                      <Col className="flex items-center justify-center">
                        <div className="bg-gradient-to-r from-transparent via-white/10 to-transparent h-px w-full hidden md:block"></div>
                        <div className="md:hidden bg-premium-darker/80 rounded-full h-12 w-12 flex items-center justify-center shadow-lg">
                          <Text className="text-xl font-bold text-white">VS</Text>
                        </div>
                        <div className="hidden md:flex bg-premium-darker/80 rounded-full h-14 w-14 items-center justify-center shadow-lg border border-white/10">
                          <Text className="text-2xl font-bold text-white">VS</Text>
                        </div>
                        <div className="bg-gradient-to-r from-transparent via-white/10 to-transparent h-px w-full hidden md:block"></div>
                      </Col>
                      
                      {/* Insurance Strategy Column */}
                      <Col className="bg-premium-darker/60 p-4 rounded-lg border border-blue-500/20 shadow-md relative overflow-hidden"> 
                        <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full translate-x-8 -translate-y-8 filter blur-md"></div>
                        <Title className="text-lg text-blue-400 mb-3 text-center font-semibold relative z-10">Corp-Owned Insurance</Title> 
                        <div className="space-y-3 relative z-10 flex flex-col h-full"> 
                          <div className="bg-premium-darkest/40 p-3 rounded-md">
                            <Text className="text-gray-400 text-sm">Final Value (Year 20)</Text>
                            <Metric className="text-blue-400 text-xl mt-1">$20,459,125</Metric>
                          </div>
                          <div className="bg-premium-darkest/40 p-3 rounded-md">
                            <Text className="text-gray-400 text-sm">Tax Impact</Text>
                            <Metric className="text-green-500 text-xl mt-1 flex items-center justify-center">
                              <span className="mr-1">$0 (Tax-Sheltered)</span>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </Metric>
                          </div>
                        </div>
                      </Col>
                    </Grid>
                  </div>

                  {/* Overall Advantage - Enhanced */}
                  <div className="text-center border-t border-white/10 pt-5 bg-gradient-to-r from-premium-darker/40 via-green-900/10 to-premium-darker/40 py-3 rounded-lg">
                      <Text className="text-gray-300 font-medium">Illustrative Advantage (Corp-Owned Insurance vs. Taxable)</Text>
                      <Metric className="text-emerald-400 text-2xl mt-1 flex items-center justify-center">
                        <span>+$6.1M (+42.7%)</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </Metric>
                  </div>
                </div>
                 <div className="bg-premium-darkest/30 p-3 rounded-md mt-4 border border-yellow-500/10">
                   <Text className="text-gray-500 text-xs italic">Note: This chart illustrates potential outcomes based on the specified assumptions (Taxable: 6% gross return, 50% tax on growth; Corp-Owned Insurance: 6.2% net return) and is not a guarantee of performance. Actual results vary based on product selection, market conditions, and individual circumstances.</Text>
                 </div>
              </Card>

              {/* Additional Performance Metrics */}
              <Grid numItemsMd={3} numItemsLg={3} className="mt-8 gap-5">
                {/* Protection Services Highlight */}
                <div 
                  className="bg-premium-darker/70 backdrop-blur-sm border border-white/10 shadow-xl rounded-xl p-6 flex items-center justify-between hover:shadow-2xl transition-shadow duration-300 cursor-pointer"
                  onClick={() => handleCardClick('protection-services')}
                >
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Protection Services</p>
                    <p className="text-white text-xl font-semibold">Risk Management</p>
                    <p className="text-gray-300 text-xs mt-1">Term, Whole & Universal Life Insurance</p>
                  </div>
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                    <ShieldIcon className="text-green-400" />
                  </div>
                </div>

                {/* Wealth & Legacy Planning */}
                <div 
                  className="bg-premium-darker/70 backdrop-blur-sm border border-white/10 shadow-xl rounded-xl p-6 flex items-center justify-between hover:shadow-2xl transition-shadow duration-300 cursor-pointer"
                  onClick={() => handleCardClick('wealth-services')}
                >
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Wealth Services</p>
                    <p className="text-white text-xl font-semibold">Asset Growth</p>
                    <p className="text-gray-300 text-xs mt-1">TFSA, RRSP & Investment Strategies</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <svg className="text-blue-400 h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>

                {/* Business Planning */}
                <div 
                  className="bg-premium-darker/70 backdrop-blur-sm border border-white/10 shadow-xl rounded-xl p-6 flex items-center justify-between hover:shadow-2xl transition-shadow duration-300 cursor-pointer"
                  onClick={() => handleCardClick('business-services')}
                >
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Business Services</p>
                    <p className="text-white text-xl font-semibold">Succession Planning</p>
                    <p className="text-gray-300 text-xs mt-1">Buy-Sell & Key Person Insurance</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                    <svg className="text-purple-400 h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                </div>
              </Grid>
            </div>
          </div>
        </section>



        {/* Call to Action */}
        <section className="py-20 bg-gradient-to-br from-blue-900 to-purple-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6 text-white">
                Ready to Get Started?
              </h2>
              <p className="text-xl mb-8 text-gray-300">
                Not sure where to begin? Use our service finder tool to get recommendations, or book a free consultation to discuss your specific needs and goals.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                {/* Services button */}
                <button 
                  onClick={() => setIsWizardModalOpen(true)}
                  className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-medium hover:shadow-lg transition-all hover:opacity-90 flex items-center gap-2"
                >
                  <span className="relative overflow-hidden">
                    <span className="absolute -rotate-12 -translate-x-full top-0 w-1/3 h-full bg-gradient-to-r from-transparent via-amber-300/30 to-transparent transform animate-shine"></span>
                  </span>
                  Find Your Service
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
                
                {/* Book a Consultation button */}
                <Link to="/contact">
                  <button 
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all hover:opacity-90"
                  >
                    Book a Consultation
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Service Detail Modal - This is the "popout card" */}
        {/* It renders when 'selectedService' state is not null */}
        {selectedService && (
          <ServiceDetailModal 
            service={selectedService} // Pass the clicked service data to the modal
            onClose={() => setSelectedService(null)} // Clicking the backdrop or close button clears the state, hiding the modal
          />
        )}

      {/* Booking Widget */}
      {showBookingWidget && (
        <BookingWidget onClose={() => setShowBookingWidget(false)} />
      )}
    </main>

    {/* Service Wizard Modal */}
    <ServiceWizard 
      services={[
        ...insuranceServices,
        ...savingsRetirementServices,
        ...businessPlanningServices,
        ...estateLegacyServices,
        ...advancedWealthServices
      ]}
      onServiceSelect={(service) => {
        setSelectedService(service);
        setIsWizardModalOpen(false);
      }}
      isOpen={isWizardModalOpen}
      onClose={() => setIsWizardModalOpen(false)}
    />
    </MarketingLayout>
  );
}
